import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, Save } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';

export default function SessionResultEditor({ booking, existing, onSaved }) {
  const [form, setForm] = useState(() => {
    const base = {
      track: '',
      session_date: booking?.date || '',
      fastest_lap_time: '',
      average_lap_time: '',
      top_speed_kph: '',
      laps_completed: '',
      final_position: '',
      summary: '',
      admin_notes: '',
    };
    if (existing) {
      return {
        ...base,
        track: existing.track || '',
        session_date: existing.session_date || booking?.date || '',
        fastest_lap_time: existing.fastest_lap_time || '',
        average_lap_time: existing.average_lap_time || '',
        top_speed_kph: existing.top_speed_kph ?? '',
        laps_completed: existing.laps_completed ?? '',
        final_position: existing.final_position ?? '',
        summary: existing.summary || '',
        admin_notes: existing.admin_notes || '',
      };
    }
    return base;
  });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        track: form.track,
        session_date: form.session_date,
        fastest_lap_time: form.fastest_lap_time,
        average_lap_time: form.average_lap_time,
        top_speed_kph: form.top_speed_kph === '' ? null : Number(form.top_speed_kph),
        laps_completed: form.laps_completed === '' ? null : Number(form.laps_completed),
        final_position: form.final_position === '' ? null : Number(form.final_position),
        summary: form.summary,
        admin_notes: form.admin_notes,
      };
      if (existing) {
        await base44.entities.SessionResult.update(existing.id, payload);
      } else {
        await base44.entities.SessionResult.create({
          ...payload,
          booking_id: booking.id,
          customer_email: booking.customer_email,
          customer_name: booking.customer_name,
          experience_type: booking.experience_type,
          rig_type: booking.rig_type,
        });
      }
      toast({ title: existing ? 'Results updated' : 'Results saved' });
      onSaved?.();
    } catch (e) {
      toast({ title: 'Failed to save', variant: 'destructive' });
    }
    setSaving(false);
  };

  const field = "bg-[#111115] border-white/10 text-white placeholder:text-white/20 focus:border-primary/60";

  return (
    <div className="bg-[#0d0d0f] border border-white/10 p-5 mt-3 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Track / Circuit</Label>
          <Input value={form.track} onChange={(e) => set('track', e.target.value)} placeholder="e.g. Silverstone GP" className={field} />
        </div>
        <div className="space-y-1.5">
          <Label>Session Date</Label>
          <Input type="date" value={form.session_date} onChange={(e) => set('session_date', e.target.value)} className={field} />
        </div>
        <div className="space-y-1.5">
          <Label>Fastest Lap Time</Label>
          <Input value={form.fastest_lap_time} onChange={(e) => set('fastest_lap_time', e.target.value)} placeholder="e.g. 1:23.456" className={field} />
        </div>
        <div className="space-y-1.5">
          <Label>Average Lap Time</Label>
          <Input value={form.average_lap_time} onChange={(e) => set('average_lap_time', e.target.value)} placeholder="e.g. 1:25.100" className={field} />
        </div>
        <div className="space-y-1.5">
          <Label>Top Speed (km/h)</Label>
          <Input type="number" value={form.top_speed_kph} onChange={(e) => set('top_speed_kph', e.target.value)} className={field} />
        </div>
        <div className="space-y-1.5">
          <Label>Laps Completed</Label>
          <Input type="number" value={form.laps_completed} onChange={(e) => set('laps_completed', e.target.value)} className={field} />
        </div>
        <div className="space-y-1.5">
          <Label>Final Position</Label>
          <Input type="number" value={form.final_position} onChange={(e) => set('final_position', e.target.value)} placeholder="e.g. 3" className={field} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Session Summary (shown to driver)</Label>
        <Textarea value={form.summary} onChange={(e) => set('summary', e.target.value)} rows={3} className={field} />
      </div>
      <div className="space-y-1.5">
        <Label>Internal Admin Notes</Label>
        <Textarea value={form.admin_notes} onChange={(e) => set('admin_notes', e.target.value)} rows={2} className={field} />
      </div>
      <button
        onClick={save}
        disabled={saving}
        className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-xs font-bold tracking-wider px-5 py-2.5 transition-all disabled:opacity-50"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {existing ? 'UPDATE RESULTS' : 'SAVE RESULTS'}
      </button>
    </div>
  );
}